import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  CancelablePromise,
  PrintExampleDto,
  PrintExampleService,
} from '@/api-sdk';
import { FgDialog } from '@/components';
import {
  iconCheckmarkCircleTransparent,
  iconTrashCan,
} from '@/components/icons';
import {
  global,
  GlobalComponentType,
  printExample,
  PrintExampleType,
  uploadService,
  UploadServiceContext,
} from '@/context';
import {
  baseStyles,
  buttonStyles,
  fileUploadStyles,
  rootVariablesStyles,
} from '@/styles';
import { ApiService, UploadStatus } from '@/types';
import { uploadFileToEndpoint } from '@/utils';

import '../../components/personalisation/step/fg-upload-file';
import '../loader/fg-loader';

@customElement('fg-print-example')
@localized()
export class FgPrintExample extends LitElement {
  private fgDialogRef: Ref<FgDialog> = createRef();
  /**
   *
   * configurationId, is the id from the configuration received from property fg-configuration
   * @property {String} configurationId
   */
  @property({ type: String })
  configurationId?: string;

  /**
   *
   * personalizable is a boolean shipped from fg-configuration, and is used to check if the product is personalizable. This is used in the component when the component is in the CE-FE cart page
   * @property {Boolean} personalizable
   */
  @property({ type: Boolean })
  personalizable = false;

  /**
   *
   * showPrintProofButton is a boolean shipped from fg-configuration, and is used to check if the printproof button must be showed. Sometimes you won't show the button but only the uploaded print proof if so there is no need to show the button.
   * @property {Boolean} showPrintProofButton
   */
  @property({ type: Boolean })
  showPrintProofButton = false;

  /**
   * printExampleData {@link PrintExampleDto} contains the print example data like uuid, filename, url
   * @property {PrintExampleDto} printExampleData
   */
  @property({ type: Object })
  printExampleData?: {
    configurationId?: string;
    data: PrintExampleDto | undefined | null;
  };

  /**
   * productName, is the name of the product
   * @property {String} productName
   */
  @property({ type: String })
  productName?: string;

  @state()
  printExampleStatus!: UploadStatus;

  @state()
  printExamplePromise?: CancelablePromise<PrintExampleDto>;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: uploadService, subscribe: true })
  uploadService: UploadServiceContext = undefined;

  @consume({ context: printExample.context, subscribe: true })
  printExampleContext?: PrintExampleType;

  async connectedCallback() {
    this.addEventListener('uploading-success', this.uploadFileDone);
    super.connectedCallback();
  }

  public openDialog() {
    this.fgDialogRef.value?.open();
  }

  public closeDialog() {
    this.fgDialogRef.value?.close();
  }

  async removePrintExampleType() {
    this.uploadService?.cancel();

    if (this.printExampleStatus === UploadStatus.SUCCESS) {
      this.printExampleStatus = UploadStatus.INITIAL;
      return;
    }

    if (
      this.configurationId === undefined ||
      (!this.printExampleData?.configurationId && this.configurationId === '')
    ) {
      this.printExampleStatus = UploadStatus.INITIAL;

      printExample.updateProviderEvent(this, undefined);
    } else {
      this.printExampleStatus = UploadStatus.INITIAL;
      await PrintExampleService.deleteRemovePrintExampleFromConfiguration({
        configurationId:
          this.printExampleData?.configurationId || this.configurationId,
      }).finally(() => {
        this.printExampleData = undefined;
        printExample.updateProviderEvent(this, undefined);
        this.requestUpdate();
      });
    }
  }

  uploadPrintExample(event: CustomEvent) {
    const formData = {
      file: event.detail,
    };

    uploadFileToEndpoint(
      this,
      formData,
      ApiService.PRINTEXAMPLE,
      'uploading-success',
      'print-example-upload-error',
    );

    this.printExampleStatus = UploadStatus.UPLOADING;
  }

  uploadFileDone(event: CustomEvent) {
    if (event.detail) {
      this.printExampleStatus = UploadStatus.SUCCESS;
      printExample.updateProviderEvent(this, event.detail);

      if (this.configurationId) {
        printExample.save(event.detail, this.configurationId);
      }
    }
  }

  render() {
    const { layout } = this.globalContext;

    return html`
      <div class="button-wrapper-ghost">
        ${this.printExampleContext || this.printExampleData?.data
          ? html`
              ${t('actions.printproofUploaded')}:
              <a
                href="${ifDefined(
                  this.printExampleContext
                    ? this.printExampleContext?.url
                    : this.printExampleData?.data?.url,
                )}"
                target="_blank"
              >
                ${this.printExampleContext
                  ? this.printExampleContext?.filename
                  : this.printExampleData?.data?.filename}
              </a>
              ${!this.configurationId ||
              (this.showPrintProofButton &&
                this.configurationId &&
                this.personalizable)
                ? html`
                    <button
                      class="remove"
                      @click=${async () => {
                        this.printExampleStatus = UploadStatus.INITIAL;
                        await this.removePrintExampleType();
                      }}
                    >
                      ${iconTrashCan}
                    </button>
                  `
                : nothing}
            `
          : !this.configurationId ||
              (this.showPrintProofButton &&
                (this.printExampleData?.configurationId ||
                  (this.configurationId && this.personalizable)))
            ? html`
                <button
                  class="button button-ghost"
                  @click=${() => this.openDialog()}
                >
                  ${t('actions.uploadYourOwnPrintproof')}
                  <span>(${t('labels.optional')})</span>
                </button>
              `
            : nothing}
      </div>
      <fg-dialog part=${layout} ${ref(this.fgDialogRef)}>
        <span slot="title">${t('actions.uploadYourOwnPrintproof')}</span>
        <div class="body">
          <h5>${this.productName}</h5>
          ${this.printExampleStatus === UploadStatus.SUCCESS
            ? html`
                <div class="upload-area green">
                  ${iconCheckmarkCircleTransparent}
                  <p class="success">
                    ${t('actions.printExampleUploaded')}
                    <br />
                    ${this.printExampleContext?.filename}
                  </p>
                  <button
                    class="button button-green"
                    @click=${() => this.closeDialog()}
                  >
                    ${t('actions.close')}
                  </button>
                </div>
              `
            : this.printExampleStatus === UploadStatus.UPLOADING
              ? html`
                  <div class="upload-area green">
                    <fg-loader
                      .bgcolor=${'transparent'}
                      type="configuration"
                      .label=${t('labels.uploading')}
                    ></fg-loader>
                  </div>
                `
              : html`
                  <fg-upload-file
                    part=${layout}
                    uploadType="printexample"
                    filecheck="pdf"
                    @upload-file=${this.uploadPrintExample}
                  ></fg-upload-file>
                `}
        </div>
        <p slot="footer">${t('content.printExample.footer')}</p>
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    fileUploadStyles,
    rootVariablesStyles,
    css`
      .body {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        margin-bottom: 1.5rem;
        padding: 0.5rem;
      }

      h5,
      p.success {
        font-size: 1.125rem;
        font-weight: 600;
      }

      h5 {
        padding-block: 2rem;
      }

      a {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        font-weight: bold;
        text-decoration: underline;
      }

      p.success {
        text-align: center;
        line-height: 1.5rem;
      }

      .upload-area svg {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        height: 48px;
        margin-bottom: 0.75rem;
        width: 48px;
      }

      button {
        margin-top: 1rem;
      }

      .button-wrapper-ghost {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        margin-top: 16px;
        width: 100%;
      }

      .remove {
        margin: 0;
      }

      .remove svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        height: 16px;
        margin-bottom: 0;
        width: 16px;
      }
    `,
  ];
}
